import { useCallback, useMemo } from 'react';
import { isValid } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { UserRegistrationPeriod, WorkType, WorkProjectType, AggregatedUser } from '../api/generated/data-contracts';
import { DateAsStartOfDay, getEmptyGuid } from '../components/Utils';
import { PeriodDay } from '../types/PeriodDay';

export const workTypeAllowed = (
    timeRow: UserRegistrationPeriod,
    workTypesEnabled: boolean,
    timeEntriesMustHaveWorkType: boolean,
    allowedWorkTypes: WorkType[],
    projectTypes: WorkProjectType[],
) => {
    if (timeRow === undefined) {
        return true;
    }

    if (!workTypesEnabled) {
        return true;
    }

    if (workTypesEnabled && !timeEntriesMustHaveWorkType) {
        return true;
    }

    if (timeEntriesMustHaveWorkType && (!timeRow.workType || timeRow.workType.id === getEmptyGuid())) {
        return false;
    }

    if (timeRow.timeEntries.some(_ => _.assignment.timeTask?.isDefault === true)) {
        return true;
    }
    const userWorkTypes = [...allowedWorkTypes];
    if (userWorkTypes.length === 0) {
        return true;
    }

    const projectWorkTypes = projectTypes.find(_ => _.id === timeRow.projectWorkType?.id)?.allowedWorkTypes || [];

    const userWorkTypesInProject = userWorkTypes.filter(uw => projectWorkTypes.some(pw => pw.id === uw.id));
    const projectWorkTypesInUser = projectWorkTypes.filter(uw => userWorkTypes.some(pw => pw.id === uw.id));
    const avaiableWorkWtype = [...projectWorkTypesInUser, ...userWorkTypesInProject].filter((v, i, a) => a.findIndex(t => t.id === v.id) === i);

    const rowWorkType = timeRow.workType;
    const workTypeAllowedvar = avaiableWorkWtype?.find(wt => wt.id === rowWorkType?.id);
    if (!workTypeAllowedvar) {
        return false;
    } else {
        return true;
    }
};

export const useIsCellReadOnly = ({
    aggregatedUser,
    allowedWorkTypes,
    onlyAllowTimeSheetWithinTaskStartEnd,
    periodDays,
    projectTypes,
    timesheetIsReadonly,
    timeEntriesMustHaveWorkType,
    workTypesEnabled,
}: {
    timesheetIsReadonly: boolean;
    onlyAllowTimeSheetWithinTaskStartEnd: boolean;
    periodDays: PeriodDay[];
    aggregatedUser: AggregatedUser;
    workTypesEnabled: boolean;
    timeEntriesMustHaveWorkType: boolean;
    allowedWorkTypes: WorkType[];
    projectTypes: WorkProjectType[];
}) => {
    const { t } = useTranslation('timesheet');

    const messages = useMemo(
        () => ({
            periodClosed: t('readOnlyMessages.periodClosed'),
            notWorkingTime: t('readOnlyMessages.notWorkingTime'),
            taskNoDates: t('readOnlyMessages.taskNoDates'),
            taskNoStart: t('readOnlyMessages.taskNoStart'),
            taskNoEnd: t('readOnlyMessages.taskNoEnd'),
            taskNotStarted: t('readOnlyMessages.taskNotStarted'),
            taskEnded: t('readOnlyMessages.taskEnded'),
            worktypeNotAllowed: t('readOnlyMessages.worktypeNotAllowed'),
            projectClosed: t('readOnlyMessages.projectClosed'),
            taskClosed: t('readOnlyMessages.taskClosed'),
        }),
        [t],
    );

    const getIfReadOnly = useCallback(
        (time: UserRegistrationPeriod, day?: PeriodDay) => {
            if (timesheetIsReadonly) {
                return messages.periodClosed;
            }

            if (isValid(aggregatedUser.user.start) && isValid(aggregatedUser.user.end)) {
                if (
                    (periodDays[0].date < new Date(aggregatedUser.user.start) && new Date(aggregatedUser.user.start).getFullYear() > 1900) ||
                    (periodDays[periodDays.length - 1].date > new Date(aggregatedUser.user.end) && new Date(aggregatedUser.user.end).getFullYear() > 1900)
                ) {
                    return messages.notWorkingTime;
                }
            }

            if (onlyAllowTimeSheetWithinTaskStartEnd && !time.taskIsDefault) {
                if (time.taskStart === undefined && time.taskEnd === undefined) {
                    return messages.taskNoDates;
                } else if (time.taskStart === undefined) {
                    return messages.taskNoStart;
                } else if (time.taskEnd === undefined) {
                    return messages.taskNoEnd;
                }

                const taskStart = DateAsStartOfDay(new Date(time.taskStart));
                if (day) {
                    if (day.date < taskStart) {
                        return messages.taskNotStarted;
                    }
                } else if (periodDays[0].date < taskStart) {
                    return messages.taskNotStarted;
                }

                const taskEnd = DateAsStartOfDay(new Date(time.taskEnd));
                if (day) {
                    if (day.date > taskEnd) {
                        return messages.taskEnded;
                    }
                } else if (periodDays[periodDays.length - 1].date > taskEnd) {
                    return messages.taskEnded;
                }
            }

            if (!workTypeAllowed(time, workTypesEnabled, timeEntriesMustHaveWorkType, allowedWorkTypes, projectTypes)) {
                return messages.worktypeNotAllowed;
            }

            if (time.projectClosed) {
                return messages.projectClosed;
            }

            if (time.taskClosed) {
                return messages.taskClosed;
            }

            return '';
        },
        [
            aggregatedUser.user.end,
            aggregatedUser.user.start,
            allowedWorkTypes,
            messages.notWorkingTime,
            messages.periodClosed,
            messages.projectClosed,
            messages.taskClosed,
            messages.taskEnded,
            messages.taskNoDates,
            messages.taskNoEnd,
            messages.taskNoStart,
            messages.taskNotStarted,
            messages.worktypeNotAllowed,
            onlyAllowTimeSheetWithinTaskStartEnd,
            periodDays,
            projectTypes,
            timesheetIsReadonly,
            timeEntriesMustHaveWorkType,
            workTypesEnabled,
        ],
    );

    return { getIfReadOnly };
};
